import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Plus, Search, Briefcase, Calendar, Users, X, TrendingUp, ArrowRight, Minus, Trash2 } from 'lucide-react'
import { getProjects, createProject as createProjectSvc, updateProject as updateProjectSvc, deleteProject as deleteProjectSvc } from '../services/projects.service'
import { useToast } from '../context/ToastContext'
import { useActivity } from '../context/ActivityContext'
import { useIsAdmin } from '../hooks/useIsAdmin'
import type { Project } from '../types'

const TEAM = ['HJ', 'Bunny', 'Darshu', 'Rahul', 'Priya', 'Arun', 'Sneha', 'Vikram']

// ── Add Project Modal ──────────────────────────────────────────
function AddProjectModal({ onClose, onAdd }: { onClose: () => void; onAdd: (p: Omit<Project, 'id'>) => void }) {
  const today = new Date().toISOString().split('T')[0]
  const [form, setForm] = useState({
    title:       '',
    clientName:  '',
    status:      'planning' as Project['status'],
    priority:    'medium' as Project['priority'],
    dueDate:     today,
    description: '',
    tagsRaw:     '',
    assigneesRaw: '',
  })
  const [error, setError] = useState('')

  const set = (k: string, v: string) => { setForm(f => ({ ...f, [k]: v })); setError('') }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim())  { setError('Project title is required.'); return }
    if (!form.dueDate)       { setError('Due date is required.'); return }
    const tags     = form.tagsRaw.split(',').map(t => t.trim()).filter(Boolean)
    const assignees = form.assigneesRaw.split(',').map(a => a.trim()).filter(Boolean)
    const newProject: Omit<Project, 'id'> = {
      title:       form.title.trim(),
      clientId:    '',
      clientName:  form.clientName.trim(),
      status:      form.status,
      priority:    form.priority,
      dueDate:     form.dueDate,
      description: form.description.trim(),
      progress:    0,
      tags,
      assignees:   assignees.length ? assignees : ['—'],
    }
    onAdd(newProject)
    onClose()
  }

  const inputCls = "w-full rounded-lg px-3 py-2 text-sm text-white outline-none"
  const inputStyle = { background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)' }
  const labelStyle = { color: '#a0a0a0' }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>New Project</h2>
          <button onClick={onClose} style={{ color: '#555' }}><X size={18} /></button>
        </div>

        {error && (
          <div className="text-xs px-3 py-2 rounded-lg mb-4" style={{ background: 'rgba(204,51,0,0.12)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.3)' }}>{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>PROJECT TITLE *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Q2 Meta Ads Overhaul"
              className={inputCls} style={inputStyle} autoFocus
              onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
              onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>CLIENT</label>
            <input value={form.clientName} onChange={e => set('clientName', e.target.value)}
              placeholder="Client name" className={inputCls} style={inputStyle}
              onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
              onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>STATUS</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}
                className={inputCls} style={inputStyle}>
                <option value="planning">Planning</option>
                <option value="in-progress">In Progress</option>
                <option value="review">Review</option>
                <option value="done">Done</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>PRIORITY</label>
              <select value={form.priority} onChange={e => set('priority', e.target.value)}
                className={inputCls} style={inputStyle}>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>DUE DATE *</label>
            <input type="date" value={form.dueDate} onChange={e => set('dueDate', e.target.value)}
              className={inputCls} style={{ ...inputStyle, colorScheme: 'dark' }}
              onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
              onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>DESCRIPTION</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Project overview…" rows={2}
              className={inputCls} style={{ ...inputStyle, resize: 'none' }}
              onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
              onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-2" style={labelStyle}>ASSIGN TEAM <span style={{ color: '#555', fontWeight: 400 }}>(comma separated)</span></label>
            <input value={form.assigneesRaw} onChange={e => set('assigneesRaw', e.target.value)}
              placeholder={TEAM.slice(0, 3).join(', ')}
              className={inputCls} style={inputStyle}
              onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
              onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
            <p className="text-xs mt-1" style={{ color: '#555' }}>Team: {TEAM.join(', ')}</p>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>TAGS <span style={{ color: '#555', fontWeight: 400 }}>(comma separated)</span></label>
            <input value={form.tagsRaw} onChange={e => set('tagsRaw', e.target.value)}
              placeholder="e.g. Google Ads, SEO, Strategy"
              className={inputCls} style={inputStyle}
              onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
              onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
              Cancel
            </button>
            <button type="submit"
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#ffc600', color: '#000' }}>
              Create Project
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const STATUS_STYLE: Record<Project['status'], { bg: string; color: string; label: string }> = {
  'planning':    { bg: 'rgba(85,85,85,0.2)',   color: '#a0a0a0', label: 'Planning' },
  'in-progress': { bg: 'rgba(255,198,0,0.12)', color: '#ffc600', label: 'In Progress' },
  'review':      { bg: 'rgba(232,160,0,0.12)', color: '#e8a000', label: 'In Review' },
  'done':        { bg: 'rgba(50,200,100,0.12)', color: '#32c864', label: 'Done' },
}

const PRIORITY_STYLE: Record<Project['priority'], { color: string }> = {
  urgent: { color: '#cc3300' },
  high:   { color: '#ff8c00' },
  medium: { color: '#ffc600' },
  low:    { color: '#555' },
}

function ProgressBar({ value, color = '#ffc600' }: { value: number; color?: string }) {
  return (
    <div className="h-1.5 rounded-full w-full" style={{ background: '#1a1a1a' }}>
      <div className="h-full rounded-full transition-all" style={{ width: `${value}%`, background: color }} />
    </div>
  )
}

function ProjectModal({ project, onClose, onUpdate, onDelete, isAdmin }: {
  project: Project
  onClose: () => void
  onUpdate: (id: string, changes: Partial<Project>) => void
  onDelete: (id: string) => void
  isAdmin: boolean
}) {
  const [progress, setProgress]       = useState(project.progress)
  const [status, setStatus]           = useState(project.status)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const st = STATUS_STYLE[status]
  const pr = PRIORITY_STYLE[project.priority]
  const isOverdue = status !== 'done' && new Date(project.dueDate) < new Date()

  const handleSave = () => {
    onUpdate(project.id, { progress, status })
    onClose()
  }

  const NEXT_STATUSES = (Object.keys(STATUS_STYLE) as Project['status'][]).filter(s => s !== status)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-md rounded-2xl overflow-hidden" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)', maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div className="p-5 border-b" style={{ borderColor: 'rgba(255,198,0,0.1)' }}>
          <div className="flex items-start justify-between">
            <div className="flex-1 mr-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs px-2 py-0.5 rounded" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                <span className="text-xs font-semibold capitalize" style={{ color: pr.color }}>● {project.priority}</span>
                {isOverdue && <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(204,51,0,0.12)', color: '#cc3300' }}>Overdue</span>}
              </div>
              <h2 className="text-base font-bold text-white font-syne leading-snug">{project.title}</h2>
              <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>{project.clientName}</p>
            </div>
            <button onClick={onClose} style={{ color: '#555' }}><X size={18} /></button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Description */}
          {project.description && (
            <p className="text-sm leading-relaxed" style={{ color: '#a0a0a0' }}>{project.description}</p>
          )}

          {/* Progress slider */}
          <div className="rounded-xl p-4" style={{ background: '#1a1a1a' }}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold" style={{ color: '#555' }}>PROGRESS</span>
              <span className="text-lg font-bold font-syne" style={{ color: '#ffc600' }}>{progress}%</span>
            </div>
            <input
              type="range" min="0" max="100" step="5" value={progress}
              onChange={e => setProgress(Number(e.target.value))}
              className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
              style={{ accentColor: '#ffc600', background: `linear-gradient(to right, #ffc600 ${progress}%, #2a2a2a ${progress}%)` }}
            />
            <div className="flex justify-between text-xs mt-1.5" style={{ color: '#333' }}>
              <span>0%</span><span>50%</span><span>100%</span>
            </div>
          </div>

          {/* Quick status change */}
          <div>
            <p className="text-xs font-semibold mb-2" style={{ color: '#555' }}>MOVE TO STATUS</p>
            <div className="flex flex-wrap gap-1.5">
              {NEXT_STATUSES.map(s => {
                const sStyle = STATUS_STYLE[s]
                return (
                  <button key={s} onClick={() => setStatus(s)}
                    className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg transition-all font-medium"
                    style={{ background: sStyle.bg, color: sStyle.color, border: `1px solid ${sStyle.color}33` }}>
                    <ArrowRight size={10} />{sStyle.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Meta */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg p-3" style={{ background: '#1a1a1a' }}>
              <p className="text-xs mb-1" style={{ color: '#555' }}>DUE DATE</p>
              <p className="text-sm font-semibold" style={{ color: isOverdue ? '#cc3300' : '#fff' }}>
                {new Date(project.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
            <div className="rounded-lg p-3" style={{ background: '#1a1a1a' }}>
              <p className="text-xs mb-1" style={{ color: '#555' }}>TEAM</p>
              <div className="flex flex-wrap gap-1">
                {project.assignees.slice(0, 3).map(a => (
                  <span key={a} className="text-xs text-white">{a}</span>
                ))}
                {project.assignees.length > 3 && (
                  <span className="text-xs" style={{ color: '#555' }}>+{project.assignees.length - 3}</span>
                )}
              </div>
            </div>
          </div>

          {/* Tags */}
          {project.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {project.tags.map(tag => (
                <span key={tag} className="text-xs px-2 py-0.5 rounded"
                  style={{ background: 'rgba(255,198,0,0.08)', color: '#ffc600' }}>{tag}</span>
              ))}
            </div>
          )}

          {/* Actions */}
          {confirmDelete ? (
            <div className="flex gap-2 pt-1">
              <button onClick={() => setConfirmDelete(false)}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                Cancel
              </button>
              {isAdmin && (
                <button onClick={() => { onDelete(project.id); onClose() }}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                  style={{ background: 'rgba(204,51,0,0.15)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.3)' }}>
                  Yes, Delete
                </button>
              )}
            </div>
          ) : (
            <div className="flex gap-3 pt-1">
              {isAdmin && (
                <button onClick={() => setConfirmDelete(true)}
                  className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold flex-shrink-0"
                  style={{ background: '#1a1a1a', color: '#cc3300', border: '1px solid rgba(204,51,0,0.2)' }}>
                  <Trash2 size={14} />
                </button>
              )}
              <button onClick={onClose}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                <Minus size={13} className="inline mr-1" />Discard
              </button>
              <button onClick={handleSave}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                style={{ background: '#ffc600', color: '#000' }}>
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

type StatusFilter = Project['status'] | 'all'
type PriorityFilter = Project['priority'] | 'all'

export default function Projects() {
  const [projects, setProjects]       = useState<Project[]>([])
  const [search, setSearch]           = useState('')
  const [statusF, setStatusF]         = useState<StatusFilter>('all')
  const [priorityF, setPriorityF]     = useState<PriorityFilter>('all')
  const [selected, setSelected]       = useState<Project | null>(null)
  const [showAdd, setShowAdd]         = useState(false)
  const { toast }       = useToast()
  const { addActivity } = useActivity()
  const isAdmin         = useIsAdmin()
  const location        = useLocation()

  useEffect(() => {
    getProjects().then(setProjects).catch(console.error)
    if ((location.state as any)?.openAdd) {
      setShowAdd(true)
      window.history.replaceState({}, '')
    }
  }, [])

  // Projects show ALL — no date range filter (a project board should always be complete)
  const filtered = projects.filter(p => {
    const matchSearch = search === '' ||
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.clientName.toLowerCase().includes(search.toLowerCase())
    const matchStatus   = statusF === 'all' || p.status === statusF
    const matchPriority = priorityF === 'all' || p.priority === priorityF
    return matchSearch && matchStatus && matchPriority
  })

  const counts = {
    planning:    projects.filter(p => p.status === 'planning').length,
    'in-progress': projects.filter(p => p.status === 'in-progress').length,
    review:      projects.filter(p => p.status === 'review').length,
    done:        projects.filter(p => p.status === 'done').length,
  }

  const avgProgress = projects.length
    ? Math.round(projects.reduce((s, p) => s + p.progress, 0) / projects.length)
    : 0

  return (
    <div className="p-6 min-h-full" style={{ background: '#000' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-syne text-white">Projects</h1>
          <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>
            {counts['in-progress']} in progress · {counts.review} in review · {counts.planning} planning · {counts.done} done
          </p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: '#ffc600', color: '#000' }}>
            <Plus size={15} /> New Project
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {Object.entries(STATUS_STYLE).map(([key, style]) => (
          <div key={key} className="rounded-xl p-4 cursor-pointer transition-all"
            style={{ background: '#0f0f0f', border: `1px solid ${statusF === key ? style.color + '55' : 'rgba(255,198,0,0.12)'}` }}
            onClick={() => setStatusF(statusF === key ? 'all' : key as StatusFilter)}>
            <p className="text-2xl font-bold font-syne" style={{ color: style.color }}>
              {counts[key as keyof typeof counts]}
            </p>
            <p className="text-xs mt-0.5" style={{ color: '#a0a0a0' }}>{style.label}</p>
          </div>
        ))}
      </div>

      {/* Search + filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#555' }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search projects or clients…"
            className="w-full pl-9 pr-4 py-2 rounded-lg text-sm outline-none text-white placeholder-gray-600"
            style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }} />
        </div>
        <div className="flex gap-1.5">
          {(['all', 'urgent', 'high', 'medium', 'low'] as const).map(p => (
            <button key={p} onClick={() => setPriorityF(p)}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium capitalize"
              style={priorityF === p
                ? { background: '#ffc600', color: '#000' }
                : { background: '#0f0f0f', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Overall progress bar */}
      <div className="rounded-xl p-4 mb-5 flex items-center gap-4"
        style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
        <TrendingUp size={16} style={{ color: '#ffc600', flexShrink: 0 }} />
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-white">Overall Portfolio Progress</span>
            <span className="text-xs font-bold" style={{ color: '#ffc600' }}>{avgProgress}%</span>
          </div>
          <ProgressBar value={avgProgress} />
        </div>
      </div>

      {/* Project cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map(project => {
          const st = STATUS_STYLE[project.status]
          const pr = PRIORITY_STYLE[project.priority]
          const isOverdue = project.status !== 'done' && new Date(project.dueDate) < new Date()
          return (
            <div key={project.id}
              className="rounded-xl p-5 cursor-pointer transition-all"
              style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(255,198,0,0.3)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,198,0,0.12)')}
              onClick={() => setSelected(project)}>

              {/* Top */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 mr-2">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="text-xs px-2 py-0.5 rounded" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                    <span className="text-xs font-semibold capitalize" style={{ color: pr.color }}>● {project.priority}</span>
                  </div>
                  <h3 className="text-sm font-bold font-syne text-white leading-snug">{project.title}</h3>
                  <p className="text-xs mt-0.5" style={{ color: '#a0a0a0' }}>{project.clientName}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold font-syne" style={{ color: '#ffc600' }}>{project.progress}%</p>
                </div>
              </div>

              {/* Progress */}
              <ProgressBar value={project.progress} color={project.status === 'done' ? '#32c864' : '#ffc600'} />

              {/* Footer */}
              <div className="flex items-center justify-between mt-3 pt-3"
                style={{ borderTop: '1px solid rgba(255,198,0,0.06)' }}>
                <div className="flex items-center gap-1.5 text-xs" style={{ color: isOverdue ? '#cc3300' : '#555' }}>
                  <Calendar size={11} />
                  {new Date(project.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  {isOverdue && <span className="font-semibold"> · Overdue</span>}
                </div>
                <div className="flex items-center gap-1 text-xs" style={{ color: '#555' }}>
                  <Users size={11} />
                  {project.assignees.slice(0, 2).join(', ')}
                  {project.assignees.length > 2 && ` +${project.assignees.length - 2}`}
                </div>
              </div>

              {/* Tags */}
              {project.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {project.tags.slice(0, 3).map(tag => (
                    <span key={tag} className="text-xs px-1.5 py-0.5 rounded"
                      style={{ background: 'rgba(255,198,0,0.06)', color: '#555', fontSize: '10px' }}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16" style={{ color: '#555' }}>
          <Briefcase size={32} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No projects match your filters.</p>
        </div>
      )}

      {selected && (
        <ProjectModal
          project={selected}
          isAdmin={isAdmin}
          onClose={() => setSelected(null)}
          onUpdate={async (id, changes) => {
            try {
              await updateProjectSvc(id, changes)
              setProjects(prev => prev.map(p => p.id === id ? { ...p, ...changes } : p))
              setSelected(prev => prev?.id === id ? { ...prev, ...changes } : prev)
              toast(`"${selected.title}" updated`, 'success')
              if (changes.status === 'done') addActivity({ type: 'project_done', emoji: '🚀', description: `Project completed: ${selected.title}`, detail: selected.clientName })
              else if (changes.status === 'in-progress') addActivity({ type: 'project_started', emoji: '▶️', description: `Project started: ${selected.title}`, detail: selected.clientName })
            } catch { toast('Update failed', 'error') }
          }}
          onDelete={async id => {
            const proj = projects.find(p => p.id === id)
            try {
              await deleteProjectSvc(id)
              setProjects(prev => prev.filter(p => p.id !== id))
              setSelected(null)
              if (proj) {
                toast(`"${proj.title}" deleted`, 'info')
                addActivity({ type: 'project_deleted', emoji: '🗑️', description: `Project deleted: ${proj.title}`, detail: proj.clientName })
              }
            } catch { toast('Delete failed', 'error') }
          }}
        />
      )}
      {showAdd && (
        <AddProjectModal
          onClose={() => setShowAdd(false)}
          onAdd={async p => {
            try {
              const saved = await createProjectSvc(p)
              setProjects(prev => [saved, ...prev])
              toast(`Project "${saved.title}" created`)
              addActivity({ type: 'project_started', emoji: '🚀', description: `New project: ${saved.title}`, detail: `${saved.clientName} · ${saved.priority} priority` })
            } catch { toast('Failed to create project', 'error') }
          }}
        />
      )}
    </div>
  )
}
